import { ButtonHTMLAttributes, forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { Loader2, LucideIcon } from 'lucide-react'
import type { ButtonSize, ButtonVariant } from './Button'

/**
 * LE BOUTON ICÔNE UNIQUE (LOT-H3).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * `label` EST OBLIGATOIRE, ET CE N'EST PAS UNE PRÉCAUTION DE CONFORT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Une icône seule n'a pas de nom accessible. L'audit a relevé deux familles de
 * boutons icône dans le produit — « icône nue sans chrome » et « icône dans un
 * carré fantôme » — dont plusieurs sans aucun libellé lisible par un lecteur
 * d'écran. Le type rend l'oubli impossible : `label` alimente `aria-label` ET
 * `title`, il n'y a pas de chemin qui les laisse vides.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE COMPOSANT N'ACCEPTE PAS LES VARIANTES DESTRUCTIVES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * > « Une action destructive porte toujours un libellé. Une icône seule ne
 * >   suffit jamais, même avec un `title`. » — DESIGN-SYSTEM §5
 *
 * La suppression d'un client sur `/admin/tenants/:id` était une icône rouge sans
 * libellé : invisible et irréversible. Plutôt que de documenter l'interdit et
 * d'espérer qu'on le lise, le type l'applique — `IconButtonVariant` exclut
 * `destructive` et `destructiveSolid`. Une suppression passe par `<Button>` avec
 * son libellé, ou par une icône `ghost` qui **ouvre** une confirmation nommée
 * (l'icône ne détruit alors rien par elle-même).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DESSIN ET CIBLE COÏNCIDENT — C'EST LE SEUL CAS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * « Un bouton icône seul est carré et fait 44 × 44 » (§3). En `md` le contrôle
 * mesure donc exactement la cible tactile, sans pseudo-élément. En `sm` (32 px,
 * pour les actions de ligne d'un tableau dense) la cible de 44 px est rétablie
 * par un pseudo-élément carré centré, qui déborde sans occuper de place.
 */

/** `destructive*` est absent VOLONTAIREMENT — voir l'en-tête. */
export type IconButtonVariant = Exclude<
  ButtonVariant,
  'destructive' | 'destructiveSolid'
>

const BASE =
  'relative inline-flex items-center justify-center rounded-control shrink-0 ' +
  'transition-colors duration-150 select-none ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'

const VARIANTS: Record<IconButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-700 focus-visible:ring-primary-500',
  secondary:
    'bg-transparent text-foreground border border-border-secondary hover:bg-background-secondary ' +
    'active:bg-background-secondary focus-visible:ring-primary-500',
  ghost:
    'bg-transparent text-foreground-secondary hover:text-foreground hover:bg-background-secondary ' +
    'active:bg-background-secondary focus-visible:ring-primary-500',
}

/** `md` : 44 × 44, dessin = cible. `sm` : 32 × 32 dessinés, cible par pseudo-élément. */
const SIZES: Record<ButtonSize, string> = {
  md: 'h-touch w-touch',
  sm: 'h-8 w-8',
}

const SM_HIT_AREA =
  'before:absolute before:left-1/2 before:top-1/2 before:h-touch before:w-touch ' +
  "before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"

/* ────────────────────────────────────────────────────────────────────────────
 * Non-chevauchement des cibles tactiles — DA-050 (LOT-RT-DA2)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La recette passe 2 a MESURÉ le défaut que le pseudo-élément de `sm` crée
 * dès que deux boutons icône sont voisins : 32 px dessinés + 8 px d'écart
 * = 40 < 44, donc les deux zones de 44 px se recouvrent, et le point MILIEU
 * de l'espace entre « Modifier » et « Supprimer » appartenait à « Supprimer »
 * (2 lignes sur 4 ; sur les 2 autres, à aucun bouton — la frontière n'était
 * même pas déterministe). Sur un iPad, viser le crayon pouvait supprimer.
 *
 * La géométrie ne se négocie pas : pour que deux cibles de 44 px centrées sur
 * des dessins de 32 px ne se touchent jamais, l'écart entre les dessins doit
 * valoir AU MOINS 44 − 32 = 12 px. `IconButtonGroup` porte cet écart ; tout
 * groupe de boutons icône `sm` passe par lui. La garde
 * `IconButton.overlap.spec.tsx` vérifie la relation et falsifie le gabarit.
 */

/** Cible tactile du §3 (`min-h-touch` / `min-w-touch`). */
export const TOUCH_TARGET_PX = 44
/** Dessin d'un bouton icône `sm` (`h-8 w-8`). */
export const ICON_BUTTON_SM_DRAWN_PX = 32
/** Écart minimal entre deux boutons icône pour que leurs cibles de 44 px ne se recouvrent JAMAIS. */
export const ICON_BUTTON_GROUP_GAP_PX = TOUCH_TARGET_PX - ICON_BUTTON_SM_DRAWN_PX
/** `gap-3` = 12 px. Écrit en clair : Tailwind ne génère pas une classe assemblée. */
export const ICON_BUTTON_GROUP_GAP_CLASS = 'gap-3'

export interface IconButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/**
 * LE conteneur d'une rangée de boutons icône (et du déclencheur `⋮` d'un
 * `ActionMenu` en taille `sm`). Son seul métier est l'écart de 12 px — il ne
 * se redéclare pas au point d'appel, sinon DA-050 revient carte par carte.
 *
 * ⚠️ Le gabarit ne déclare PAS `shrink-0` : deux utilitaires du même groupe
 * s'arbitrent par leur ordre dans la feuille COMPILÉE, et un `shrink-0` de
 * gabarit annulerait le `shrink` d'un appelant (piège mesuré par H11 sur
 * `HEADER_CHIP`). Chaque appelant déclare s'il cède.
 */
export function IconButtonGroup({ children, className = '', ...rest }: IconButtonGroupProps) {
  return (
    <div
      data-testid="icon-button-group"
      className={`flex items-center ${ICON_BUTTON_GROUP_GAP_CLASS} ${className}`.trim()}
      {...rest}
    >
      {children}
    </div>
  )
}

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label' | 'title'> {
  /** Icône affichée. Purement décorative : c'est `label` qui nomme le bouton. */
  icon: LucideIcon
  /** Nom accessible. OBLIGATOIRE — alimente `aria-label` et `title`. */
  label: string
  variant?: IconButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon: Icon,
      label,
      variant = 'ghost',
      size = 'md',
      loading = false,
      disabled,
      className = '',
      type = 'button',
      ...rest
    },
    ref
  ) {
    const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-label={label}
        title={label}
        aria-busy={loading || undefined}
        className={[
          BASE,
          VARIANTS[variant],
          SIZES[size],
          size === 'sm' ? SM_HIT_AREA : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {loading ? (
          <Loader2 className={`${iconSize} animate-spin`} aria-hidden="true" />
        ) : (
          <Icon className={iconSize} aria-hidden="true" />
        )}
      </button>
    )
  }
)

export default IconButton
